import React, { Fragment } from 'react';
import {
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Skeleton,
  Typography,
  Divider,
  ListItemIcon,
} from '@mui/material';
import { useRecoilValue } from 'recoil';
import { accountSelectedSelector } from 'atoms';
import { gql, useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { Zone } from 'types/zone';
import ZoneIcon from './ZoneIcon';
import EmptyState from 'components/EmptyState';

const ZONES = gql`
  query Zones($isCloud: Boolean!, $accountId: Int!, $masterCompanyId: Int!) {
    zonesByAccountId(isCloud: $isCloud, accountId: $accountId, masterCompanyId: $masterCompanyId) {
      id
      code
      name
    }
  }
`;

function ZonesPage() {
  const { t } = useTranslation(['_common', 'zones']);
  const accountSelected = useRecoilValue(accountSelectedSelector);

  const { data, error, loading } = useQuery<{ zonesByAccountId: Zone[] }>(ZONES, {
    variables: {
      ...accountSelected,
      accountId: accountSelected?.id || 0,
    },
  });

  if (error) {
    throw error;
  }

  const haveData = data && data.zonesByAccountId?.length > 0;
  const isEmpty = !loading && !haveData;

  return (
    <Paper
      variant="outlined"
      sx={{
        pt: 2,
        height: isEmpty ? '100%' : undefined,
        maxHeight: '100%',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        mb: 2,
        boxSizing: 'border-box',
      }}
    >
      <Typography variant="h6" sx={{ px: 2 }}>
        {t('_common:zones')}
      </Typography>
      {loading && <ZonesLoading />}
      {isEmpty && (
        <EmptyState
          title={t('_common:there_is_still_nothing_here')}
          description={t('zones:there_are_no_registered_zones_for_this_account')}
          sx={{ flex: '1 1 auto' }}
        />
      )}
      {data && (
        <List sx={{ overflow: 'auto' }}>
          {data.zonesByAccountId.map((item, index) => (
            <Fragment key={item.id}>
              <ListItem>
                <ListItemIcon
                  sx={{
                    border: `1px #5958CF solid`,
                    borderRadius: '50%',
                    height: 45,
                    width: 45,
                    minWidth: 'unset',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <ZoneIcon />
                </ListItemIcon>
                <ListItemText primary={item.name} secondary={item.code} sx={{ ml: 1.5 }} />
              </ListItem>
              {index < data.zonesByAccountId.length - 1 && <Divider variant="middle" />}
            </Fragment>
          ))}
        </List>
      )}
    </Paper>
  );
}

function ZonesLoading() {
  return (
    <List>
      <ListItem>
        <ListItemAvatar>
          <Skeleton variant="circular" width={45} height={45} />
        </ListItemAvatar>
        <ListItemText primary={<Skeleton />} />
      </ListItem>
    </List>
  );
}

export default ZonesPage;
